import React from "react";
import { User, Camera, Upload } from "lucide-react";
import type { IUserDetail } from "../../../../types";
import { SelectOption } from "../../../common/Select";
import { TextField, SelectField } from "./FormFields";
import { ValidationErrors } from "./formDefaults";
import { capitalizeWords } from "../../../../utils/nameUtils";
import SignedImage from "../../../common/SignedImage";

interface BasicInfoSectionProps {
  formData: Partial<IUserDetail>;
  formErrors: Record<string, string>;
  genderOptions: SelectOption[];
  managerOptions: SelectOption[];
  isViewMode: boolean;
  selfEditMode?: boolean;
  profilePicPreview: string | null;
  profilePicUploading?: boolean;
  onProfilePicChange: (event: React.ChangeEvent<HTMLInputElement>) => void;
  onProfilePicRemove: () => void;
  onFieldChange: <K extends keyof IUserDetail>(
    field: K,
    value: IUserDetail[K]
  ) => void;
  setFormErrors: React.Dispatch<React.SetStateAction<ValidationErrors>>;
  getValidationErrors: (data: Partial<IUserDetail>) => ValidationErrors;
  markFieldTouched: (fieldName: string) => void;
}

const BasicInfoSection: React.FC<BasicInfoSectionProps> = ({
  formData,
  formErrors,
  genderOptions,
  managerOptions,
  isViewMode,
  selfEditMode,
  profilePicPreview,
  profilePicUploading = false,
  onProfilePicChange,
  onProfilePicRemove,
  onFieldChange,
  setFormErrors,
  markFieldTouched,
}) => {
  const [imgError, setImgError] = React.useState(false);

  // Reset image error state when profile picture changes
  React.useEffect(() => {
    setImgError(false);
  }, [profilePicPreview, formData.profilePic]);

  const handleFieldBlur = (field: keyof IUserDetail) => {
    // Just mark as touched, the global useEffect will handle validation
    markFieldTouched(field);
  };

  const handleFieldChange = <K extends keyof IUserDetail>(
    field: K,
    value: IUserDetail[K]
  ) => {
    // Update the form data
    onFieldChange(field, value);

    // Immediately clear the error for this field if a value is entered
    if (value !== undefined && value !== null && value !== "") {
      setFormErrors((prev: ValidationErrors) => ({
        ...prev,
        [field]: "",
      }));
    }
  };

  const handleSelectChange = <K extends keyof IUserDetail>(
    field: K,
    value: IUserDetail[K]
  ) => {
    // Update the form data
    onFieldChange(field, value);

    // Immediately clear the error for this field if a value is selected
    if (value) {
      setFormErrors((prev: ValidationErrors) => ({
        ...prev,
        [field]: "",
      }));
    }
  };

  return (
    <section className="space-y-4">
      {/* Profile Photo - Figma Match */}
      <div className="flex items-center gap-4 py-1">
        <div className="relative group flex-none">
          <div className="w-[82px] h-[82px] rounded-[18px] bg-[var(--color-primary-50)] overflow-hidden flex items-center justify-center border-[1.5px] border-dashed border-[var(--color-primary-300)]">
            {profilePicUploading && (
              <div className="absolute inset-0 bg-[var(--color-text-primary)]/50 flex items-center justify-center rounded-[18px] z-10">
                <span className="text-xs text-[var(--color-text-inverse)] font-medium">Uploading…</span>
              </div>
            )}
            {profilePicPreview && !imgError ? (
              <img
                src={profilePicPreview}
                alt="Profile preview"
                className="w-full h-full object-cover rounded-[18px]"
                onError={() => setImgError(true)}
              />
            ) : formData.profilePic && !imgError ? (
              <SignedImage
                rawUrl={formData.profilePic}
                alt="Profile"
                className="w-full h-full object-cover rounded-[18px]"
                onError={() => setImgError(true)}
              />
            ) : (
              <Camera className="w-7 h-7 text-[var(--color-primary-600)]" />
            )}
          </div>
          {!isViewMode && (
            <label
              htmlFor="profile-upload"
              className="absolute inset-0 rounded-[18px] bg-[var(--color-scrim-soft)] flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity cursor-pointer"
            >
              <Camera className="w-6 h-6 text-[var(--color-text-inverse)]" />
            </label>
          )}
        </div>

        <div className="flex-1 min-w-0">
          <h5 className="text-body-md font-bold text-[var(--color-text-primary)] leading-tight">
            Profile photo
          </h5>
          <p className="text-body-sm text-[var(--color-text-tertiary)] mt-0.5 leading-snug">
            JPG or PNG, up to 2MB. Square image recommended.
          </p>

          {!isViewMode && (
            <div className="flex items-center gap-2 mt-2">
              <label
                htmlFor="profile-upload"
                className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-[9px] bg-[var(--color-bg-base)] border border-[var(--color-border-primary)] shadow-2xs text-body-sm font-semibold text-[var(--color-text-primary)] hover:bg-[var(--color-bg-muted)] transition-colors cursor-pointer"
              >
                <Upload className="w-3.5 h-3.5 text-[var(--color-text-secondary)]" />
                <span>
                  {profilePicPreview || formData.profilePic ? "Change" : "Upload"}
                </span>
              </label>
              <input
                id="profile-upload"
                type="file"
                accept="image/*"
                className="hidden"
                onChange={onProfilePicChange}
              />
              {(profilePicPreview || formData.profilePic) && (
                <button
                  type="button"
                  onClick={onProfilePicRemove}
                  className="inline-flex items-center px-2 py-1.5 text-body-sm font-medium text-[var(--color-error)] hover:bg-[var(--color-error-light)] rounded-[8px] transition-colors cursor-pointer border-none bg-transparent"
                >
                  Remove
                </button>
              )}
            </div>
          )}
        </div>
      </div>

      <div
        className={`grid grid-cols-1 gap-4 ${
          isViewMode ? "md:grid-cols-2" : "md:grid-cols-3"
        }`}
      >
        <TextField
          label="Employee ID"
          value={formData.employeeId}
          onChange={(value) => handleFieldChange("employeeId", value)}
          disabled={isViewMode || selfEditMode}
          required
          onBlur={() => handleFieldBlur("employeeId")}
          error={formErrors.employeeId}
        />
        <TextField
          label="First Name"
          value={formData.firstName}
          onChange={(value) => handleFieldChange("firstName", capitalizeWords(value))}
          required
          onBlur={() => handleFieldBlur("firstName")}
          disabled={isViewMode || selfEditMode}
          error={formErrors.firstName}
        />
        <TextField
          label="Last Name"
          value={formData.lastName}
          onChange={(value) => handleFieldChange("lastName",capitalizeWords( value))}
          onBlur={() => handleFieldBlur("lastName")}
          disabled={isViewMode || selfEditMode}
          error={formErrors.lastName}
        />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <TextField
          label="Father's Name"
          placeholder="Enter father's name"
          value={formData.fatherName}
          onChange={(value) => handleFieldChange("fatherName", value)}
          disabled={isViewMode}
        />
        <TextField
          label="Mother's Name"
          placeholder="Enter mother's name"
          value={formData.motherName}
          onChange={(value) => handleFieldChange("motherName", value)}
          disabled={isViewMode}
        />
      </div>

      <TextField
        label="Email"
        type="email"
        placeholder="Enter your email address"
        value={formData.email}
        onChange={(value) => handleFieldChange("email", value)}
        onBlur={() => handleFieldBlur("email")}
        disabled={isViewMode}
        error={formErrors.email}
      />

      <TextField
        label="Work Email"
        type="email"
        placeholder="Enter your work email address"
        value={formData.workEmail}
        onChange={(value) => handleFieldChange("workEmail", value)}
        required
        onBlur={() => handleFieldBlur("workEmail")}
        disabled={isViewMode || selfEditMode}
        error={formErrors.workEmail}
      />

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <TextField
          label="Phone"
          placeholder="9876543210"
          value={formData.phone}
          onChange={(value) =>
            handleFieldChange("phone", value.replace(/[^\d]/g, "").slice(0, 10))
          }
          required
          onBlur={() => handleFieldBlur("phone")}
          disabled={isViewMode}
          error={formErrors.phone}
        />
        <SelectField
          label="Gender"
          value={formData.gender}
          options={genderOptions}
          onChange={(value) =>
            handleSelectChange("gender", value as string)
          }
          onBlur={() => handleFieldBlur("gender")}
          required
          disabled={isViewMode}
          error={formErrors.gender}
        />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <SelectField
          label="Reporting Manager"
          value={formData.reportingManagerId as string | undefined}
          options={managerOptions}
          onChange={(value) => handleSelectChange("reportingManagerId", value)}
          clearable
          searchable
          disabled={isViewMode || selfEditMode}
        />
        <SelectField
          label="Functional Manager"
          value={formData.functionalManagerId as string | undefined}
          options={managerOptions}
          onChange={(value) => handleSelectChange("functionalManagerId", value)}
          clearable
          searchable
          disabled={isViewMode || selfEditMode}
        />
      </div>
    </section>
  );
};

export default BasicInfoSection;
